import React, { useState, useEffect, useMemo } from "react";
import NavbarPag from "../components/NavbarPag";

interface ClientUser {
  id: string;
  rut: string;
  name: string;
  email: string;
  active: boolean;
}

const apiBase =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5001"
    : "https://api-kdps2abp6q-uc.a.run.app";

const AdminClients = () => {
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [toggling, setToggling] = useState<string | null>(null);
  const [editing, setEditing] = useState<ClientUser | null>(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", rut: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");
  const [deleting, setDeleting] = useState<ClientUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const itemsPerPage = 10;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/getUsers`);
      const data = await res.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleToggle = async (user: ClientUser) => {
    setToggling(user.id);
    try {
      const res = await fetch(`${apiBase}/setUserActive`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id, active: !user.active }),
      });
      if (!res.ok) throw new Error("Error al actualizar");
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, active: !u.active } : u))
      );
    } catch (error) {
      console.error("Error toggling user:", error);
      alert("Error al actualizar el estado del cliente.");
    } finally {
      setToggling(null);
    }
  };

  const openEdit = (user: ClientUser) => {
    setEditError("");
    setEditForm({ name: user.name, email: user.email, rut: user.rut });
    setEditing(user);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (editForm.name.trim() === "" || editForm.rut.trim() === "") {
      setEditError("Nombre y RUT son obligatorios.");
      return;
    }
    setSavingEdit(true);
    setEditError("");
    try {
      const res = await fetch(`${apiBase}/updateUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: editing.id,
          name: editForm.name.trim(),
          email: editForm.email.trim(),
          rut: editForm.rut.trim(),
        }),
      });
      if (!res.ok) throw new Error("Error al guardar");
      setUsers((prev) =>
        prev.map((u) =>
          u.id === editing.id
            ? {
                ...u,
                name: editForm.name.trim(),
                email: editForm.email.trim(),
                rut: editForm.rut.trim(),
              }
            : u
        )
      );
      setEditing(null);
    } catch (error) {
      console.error("Error updating user:", error);
      setEditError("No se pudo actualizar el cliente.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${apiBase}/deleteUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: deleting.id }),
      });
      if (!res.ok) throw new Error("Error al eliminar");
      setUsers((prev) => prev.filter((u) => u.id !== deleting.id));
      setDeleting(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("No se pudo eliminar el cliente.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = filterText.toLowerCase();
    return users.filter(
      (u) =>
        !q ||
        u.name.toLowerCase().includes(q) ||
        u.rut.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [users, filterText]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="w-screen min-h-screen bg-neutral-200 flex flex-col items-center">
      <NavbarPag />

      <div className="w-11/12 max-w-5xl mt-6 pb-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Gestión de Clientes</h1>
        </div>

        {/* Filtro */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Buscar por nombre, RUT o email
            </label>
            <input
              type="text"
              value={filterText}
              onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              placeholder="Buscar..."
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          {filterText && (
            <button
              onClick={() => { setFilterText(""); setCurrentPage(1); }}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded text-sm"
            >
              Limpiar
            </button>
          )}
          <span className="text-xs text-gray-400 self-end pb-2">
            {filtered.length} cliente{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <p className="text-center text-gray-500 py-12">Cargando clientes...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-12">
              {users.length === 0 ? "No hay clientes registrados" : "Sin resultados"}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b-2 border-gray-200">
                    <tr>
                      <th className="p-3 text-left text-sm font-semibold text-gray-600">RUT</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-600">Nombre</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-600">Email</th>
                      <th className="p-3 text-center text-sm font-semibold text-gray-600">Estado</th>
                      <th className="p-3 text-center text-sm font-semibold text-gray-600">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((user) => (
                      <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-3 text-sm font-mono">{user.rut}</td>
                        <td className="p-3 text-sm">{user.name}</td>
                        <td className="p-3 text-sm text-gray-600">{user.email}</td>
                        <td className="p-3 text-center">
                          <span
                            className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                              user.active
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {user.active ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            <button
                              onClick={() => openEdit(user)}
                              className="px-3 py-1 rounded text-xs font-semibold bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                            >
                              Editar
                            </button>
                            <button
                              disabled={toggling === user.id}
                              onClick={() => handleToggle(user)}
                              className={`px-3 py-1 rounded text-xs font-semibold transition-colors disabled:opacity-50 ${
                                user.active
                                  ? "bg-amber-500 hover:bg-amber-600 text-white"
                                  : "bg-green-500 hover:bg-green-600 text-white"
                              }`}
                            >
                              {toggling === user.id
                                ? "..."
                                : user.active
                                ? "Desactivar"
                                : "Activar"}
                            </button>
                            <button
                              onClick={() => setDeleting(user)}
                              className="px-3 py-1 rounded text-xs font-semibold bg-red-500 hover:bg-red-600 text-white transition-colors"
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-center items-center gap-3 p-4 border-t border-gray-100">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm"
                >
                  ← Anterior
                </button>
                <span className="text-sm font-semibold text-gray-600">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm"
                >
                  Siguiente →
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal Editar cliente */}
      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Editar cliente</h2>
              <button
                onClick={() => setEditing(null)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Nombre</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">RUT</label>
                <input
                  type="text"
                  value={editForm.rut}
                  onChange={(e) => setEditForm({ ...editForm, rut: e.target.value })}
                  className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {editError && (
                <div className="bg-red-100 text-red-700 text-sm px-3 py-2 rounded">
                  {editError}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 rounded text-sm bg-gray-200 hover:bg-gray-300 text-gray-700"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  className="px-4 py-2 rounded text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold"
                >
                  {savingEdit ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminación de cliente */}
      {deleting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-md p-6">
            <h2 className="text-xl font-bold mb-3">Eliminar cliente</h2>
            <p className="text-sm text-gray-700 mb-5">
              Se eliminará el cliente{" "}
              <span className="font-semibold">{deleting.name || deleting.rut}</span>.
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleting(null)}
                className="px-4 py-2 rounded text-sm bg-gray-200 hover:bg-gray-300 text-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="px-4 py-2 rounded text-sm bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-semibold"
              >
                {deleteLoading ? "Eliminando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClients;
